import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const StorageComponent = () => {
    const { user } = useUser();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/databaseStats`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setStats(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching database stats');
            } finally {
                setLoading(false);
            }
        };

        if (user.token) {
            fetchStats();
        }
    }, [user]);

    if (!user.token || !user.hasRoles(['jedimaster'])) {
        return <p>Only administrators can view the database dashboard.</p>;
    }

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const renderTable = (title, rows, columns) => (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col, index) => (
                                <TableCell key={index}>{col}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={index}>
                                {columns.map((col, colIndex) => (
                                    <TableCell key={colIndex}>{row[col]}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <div>
            <Typography variant="body1" gutterBottom>
                Total Database Size: {stats?.databaseSize?.total_database_size}
            </Typography>

            {renderTable('Data table', stats.dataRows, [
                'data_5s_count',
                'data_50s_count',
                'data_500s_count'
            ])}

            {renderTable('Tables', stats.tables, [
                'table_full_name',
                'total_size',
                'table_size',
                'indexes_size',
                'toast_size'
            ])}

            {renderTable('Compression', stats.compression, [
                'table_type',
                'total_size',
                'table_size',
                'indexes_size',
                'toast_size'
            ])}

            {renderTable('Continuous Aggregates', stats.continuousAggregates, [
                'view_name',
                'materialized_only'
            ])}

            {renderTable('Index Sizes', stats.indexSizes, ['index_name', 'index_size'])}

            {renderTable('TOAST Tables', stats.toastTables, ['toast_table', 'total_size'])}
        </div>
    );
};

export default StorageComponent;
